import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, fromEvent } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, takeUntil } from 'rxjs/operators';
import { ROUTES } from './side_nav_routes.config';
import { ThemeConstantService } from '../../services/theme_constant.service';
import { SideNavInterface } from '../../interfaces/side_nav.type';
import { ProductLicensesService, ProductLicenseSummary } from '../../services/product_licenses.service';
import { LoginService } from '../../services/login.service';
import { decodeAssessmentLicenseId, encodeBase64RouteValue } from '../../utils/base64_route_value.util';

@Component({
    selector: 'app-sidenav',
    templateUrl: './side_nav.component.html'
})
export class SideNavComponent implements OnInit, OnDestroy {
    private static readonly ASSESSMENT_ID_QUERY_PARAM = "productLicenseId";
    menuItems: SideNavInterface[] = [];

    isFolded: boolean;
    isSideNavDark: boolean;
    isExpand: boolean;
    isCompactView: boolean;
    canAccessMerchants = false;
    isFileUploader = false;
    private openMenuKeys = new Set<string>();
    private autoOpenMenuKeys = new Set<string>();
    private destroy$ = new Subject<void>();

    private readonly FOLD_THRESHOLD = 1700;
    private readonly COMPACT_BREAKPOINT = 992;

    constructor(
        private themeService: ThemeConstantService,
        private router: Router,
        private productLicensesService: ProductLicensesService,
        private loginService: LoginService
    ) {}

    ngOnInit(): void {
        this.canAccessMerchants = this.loginService.hasAdminPrivileges();
        this.isFileUploader = this.loginService.isFileUploader();
        this.menuItems = this.getFilteredRoutes(ROUTES);
        this.subscribeToProductLicenses();
        this.themeService.isMenuFoldedChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(isFolded => (this.isFolded = isFolded));
        this.themeService.isExpandChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(isExpand => (this.isExpand = isExpand));
        this.themeService.isSideNavDarkChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(isDark => (this.isSideNavDark = isDark));

        this.isCompactView = window.innerWidth < this.COMPACT_BREAKPOINT;

        fromEvent(window, 'resize')
            .pipe(
                startWith(null),
                map(() => window.innerWidth < this.COMPACT_BREAKPOINT),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(isCompact => {
                this.isCompactView = isCompact;
                const targetFoldState = this.shouldMenuBeFolded(window.innerWidth);
                if (this.isFolded !== targetFoldState) {
                    this.themeService.toggleFold(targetFoldState);
                }
            });

        this.expandForUrl();
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.expandForUrl();
                this.closeOnNavigation();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private getFilteredRoutes(routes: SideNavInterface[]): SideNavInterface[] {
        return routes
            .filter(route => this.shouldIncludeRoute(route))
            .map(route => {
                const clonedRoute: SideNavInterface = {
                    ...route,
                    submenu: [...(route.submenu || [])],
                    queryParams: route.queryParams ? { ...route.queryParams } : undefined
                };

                if (clonedRoute.submenu && clonedRoute.submenu.length > 0) {
                    clonedRoute.submenu = this.getFilteredRoutes(clonedRoute.submenu);
                }

                return clonedRoute;
            });
    }

    private shouldIncludeRoute(route: SideNavInterface): boolean {
        if (this.isFileUploader) {
            return route.path === "/secure-storage";
        }

        if (route.requiresAdmin && !this.canAccessMerchants) {
            return false;
        }

        return true;
    }

    private subscribeToProductLicenses(): void {
        this.productLicensesService
            .watchLicenses()
            .pipe(takeUntil(this.destroy$))
            .subscribe(licenses => this.updateComplianceFormsMenu(licenses));
    }

    private updateComplianceFormsMenu(licenses: ProductLicenseSummary[]): void {
        const complianceRoute = this.menuItems.find(item => item.title === "Assessments");
        if (!complianceRoute) {
            return;
        }

        const merchantMap = new Map<string, SideNavInterface>();

        const sortedLicenses = licenses
            .filter(license => this.shouldIncludeLicenseInMenu(license))
            .sort((a, b) => {
                const merchantA = (a.merchantName || "Unassigned Merchant/SP").toLowerCase();
                const merchantB = (b.merchantName || "Unassigned Merchant/SP").toLowerCase();
                if (merchantA !== merchantB) {
                    return merchantA.localeCompare(merchantB);
                }
                const titleA = (a.formType || "").toLowerCase();
                const titleB = (b.formType || "").toLowerCase();
                return titleA.localeCompare(titleB);
            });

        for (const license of sortedLicenses) {
            const routeConfig = this.resolveRouteForLicense(license);
            if (!routeConfig) {
                continue;
            }

            const merchantName = license.merchantName || "Unassigned Merchant/SP";
            const displayName = license.displayName || license.formType || "Assessment";
            const merchantKey = `${license.merchantId ?? "none"}::${merchantName.toLowerCase()}`;
            let merchantEntry = merchantMap.get(merchantKey);
            if (!merchantEntry) {
                merchantEntry = {
                    path: "",
                    title: merchantName,
                    iconType: "",
                    iconTheme: "",
                    icon: "",
                    submenu: [],
                    queryParams: null
                };
                merchantMap.set(merchantKey, merchantEntry);
            }

            merchantEntry.submenu = [
                ...merchantEntry.submenu,
                {
                    path: routeConfig.path,
                    title: displayName,
                    subtitle: license.formType || "Assessment",
                    iconType: "",
                    iconTheme: "",
                    icon: "",
                    submenu: [],
                    queryParams: routeConfig.queryParams
                }
            ];
        }

        const merchants: SideNavInterface[] = Array.from(merchantMap.values()).map(entry => ({
            ...entry,
            submenu: [...entry.submenu].sort((a, b) => a.title.localeCompare(b.title))
        }));

        merchants.sort((a, b) => a.title.localeCompare(b.title));

        const startNewAssessment: SideNavInterface = {
            path: "/add-assessment",
            title: "New Assessment",
            iconType: "nzIcon",
            iconTheme: "outline",
            icon: "plus",
            submenu: [],
            queryParams: null
        };

        complianceRoute.submenu = [...merchants, startNewAssessment];
        this.expandForUrl();
    }

    private resolveRouteForLicense(
        license: ProductLicenseSummary
    ): { path: string; queryParams: Record<string, unknown> } | null {
        const formKey = this.resolveFormKey(license);
        const productLicenseId = license.id;

        if (!Number.isInteger(productLicenseId) || productLicenseId <= 0) {
            return null;
        }

        if (formKey === "saq_form") {
            return {
                path: "/saq_form",
                queryParams: { productLicenseId: encodeBase64RouteValue(String(productLicenseId)) }
            };
        }

        if (formKey === "roc_form") {
            return {
                path: "/roc_form",
                queryParams: { productLicenseId: encodeBase64RouteValue(String(productLicenseId)) }
            };
        }

        return null;
    }

    private shouldIncludeLicenseInMenu(license: ProductLicenseSummary): boolean {
        if (!license.expirationDate) {
            return false;
        }

        const expirationTime = Date.parse(license.expirationDate);
        if (!Number.isFinite(expirationTime)) {
            return false;
        }

        return expirationTime > Date.now();
    }

    private resolveFormKey(license: ProductLicenseSummary): string | null {
        const explicitKey = (license.formKey || "").trim().toLowerCase();
        if (explicitKey) {
            return explicitKey;
        }

        const normalizedType = (license.formType || "").trim().toLowerCase();
        if (!normalizedType) {
            return null;
        }

        if (this.stringContainsAny(normalizedType, ["saq", "self-assessment questionnaire", "self assessment questionnaire"])) {
            return "saq_form";
        }

        if (this.stringContainsAny(normalizedType, ["roc", "report on compliance"])) {
            return "roc_form";
        }

        return null;
    }

    private stringContainsAny(value: string, needles: string[]): boolean {
        return needles.some(needle => value.includes(needle));
    }

    toggleSubmenu(route: SideNavInterface, event: Event): void {
        if (!route.submenu || route.submenu.length === 0) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        const key = this.getRouteKey(route);
        if (this.openMenuKeys.has(key)) {
            this.openMenuKeys.delete(key);
            this.autoOpenMenuKeys.delete(key);
        } else {
            this.openMenuKeys.add(key);
            this.autoOpenMenuKeys.delete(key);
        }
    }

    isSubmenuOpen(route: SideNavInterface): boolean {
        return this.openMenuKeys.has(this.getRouteKey(route));
    }

    private getRouteKey(route: SideNavInterface): string {
        if (route.path) {
            return this.normalizeUrl(route.path);
        }

        return `title:${route.title}`;
    }

    private expandForUrl(): void {
        this.autoOpenMenuKeys.forEach(key => this.openMenuKeys.delete(key));
        this.autoOpenMenuKeys.clear();

        this.menuItems.forEach(item => {
            const key = this.getRouteKey(item);
            if (this.hasActiveChild(item)) {
                this.openMenuKeys.add(key);
                this.autoOpenMenuKeys.add(key);
            }
        });
    }

    closeMobileMenu(): void {
        if (window.innerWidth < 1440) {
            if (this.isExpand) {
                this.isExpand = false;
                this.themeService.toggleExpand(false);
            }

            const targetFoldState = this.shouldMenuBeFolded(window.innerWidth);
            if (this.isFolded !== targetFoldState) {
                this.themeService.toggleFold(targetFoldState);
            }
        }
    }

    hasActiveChild(route: SideNavInterface): boolean {
        if (!route.submenu || route.submenu.length === 0) {
            return false;
        }

        return route.submenu.some(subItem => {
            if (this.isRouteActive(subItem)) {
                return true;
            }

            return this.hasActiveChild(subItem);
        });
    }

    isRouteSelected(route: SideNavInterface): boolean {
        return this.isRouteActive(route);
    }

    private isRouteActive(route: SideNavInterface): boolean {
        if (!route.path) {
            return false;
        }

        const pathTree = this.router.createUrlTree([route.path]);
        const isPathActive = this.router.isActive(pathTree, {
            paths: "exact",
            queryParams: "ignored",
            fragment: "ignored",
            matrixParams: "ignored"
        });

        if (!isPathActive) {
            return false;
        }

        const activeQueryParams = this.getActiveQueryParams();
        const queryParams = this.normalizeQueryParams(route.queryParams);
        for (const [key, value] of Object.entries(queryParams)) {
            if (key === SideNavComponent.ASSESSMENT_ID_QUERY_PARAM) {
                if (!this.isMatchingAssessmentLicenseId(activeQueryParams[key], value)) {
                    return false;
                }
                continue;
            }

            if (activeQueryParams[key] !== value) {
                return false;
            }
        }

        return true;
    }

    private normalizeUrl(url: string): string {
        const [cleaned] = url.split("?");
        return cleaned.replace(/\/$/, "");
    }

    private closeOnNavigation(): void {
        if (!this.isExpand) {
            return;
        }

        this.isExpand = false;
        this.themeService.toggleExpand(false);

        const targetFoldState = this.shouldMenuBeFolded(window.innerWidth);
        if (this.isFolded !== targetFoldState) {
            this.themeService.toggleFold(targetFoldState);
        }
    }

    private shouldMenuBeFolded(width: number): boolean {
        if (width < this.COMPACT_BREAKPOINT) {
            return true;
        }

        return width < this.FOLD_THRESHOLD;
    }

    private getActiveQueryParams(): Record<string, string> {
        const parsedUrl = this.router.parseUrl(this.router.url);
        const queryParams = parsedUrl?.queryParams ?? {};
        return this.normalizeQueryParams(queryParams);
    }

    // Assessment routes should remain selected even if equivalent values arrive in
    // base64 form or plain numeric form. Compare normalized numeric ids.
    private isMatchingAssessmentLicenseId(activeValue: unknown, requiredValue: unknown): boolean {
        const activeLicenseId = this.parseEncodedLicenseId(activeValue);
        const requiredLicenseId = this.parseEncodedLicenseId(requiredValue);
        return activeLicenseId !== null && requiredLicenseId !== null && activeLicenseId === requiredLicenseId;
    }

    private parseEncodedLicenseId(value: unknown): number | null {
        if (typeof value !== "string" || !value.trim()) {
            return null;
        }

        return decodeAssessmentLicenseId(value);
    }

    private normalizeQueryParams(queryParams: Record<string, unknown> | null | undefined): Record<string, string> {
        if (!queryParams) {
            return {};
        }

        const normalizedParams: Record<string, string> = {};
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value === null || value === undefined) {
                return;
            }

            normalizedParams[key] = String(value);
        });

        return normalizedParams;
    }
}
